<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Guest gate fired from the commission card on a property page.
 * Shows a teaser commission estimate to entice signup.
 */

const { t } = useI18n();
const { percent: topSplitPercent } = useTopSplitPercent();

const { projectTitle, teaserAed = '31,200' } = defineProps<{
  projectTitle?: string;
  teaserAed?: string;
}>();

const projectTitleText = computed(() => projectTitle ?? t('property.access.modalGuestCommission.defaultProjectTitle'));

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'signup-click'): void;
  (e: 'signin-click'): void;
}>();

const titleId = useId();
</script>

<template>
  <ModalShell
    accent="green"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="commission"
      accent="green"
    />
    <div class="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-success">
      {{ t('property.access.modalGuestCommission.eyebrow') }}
    </div>
    <i18n-t
      keypath="property.access.modalGuestCommission.heading"
      tag="h2"
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      <template #project>{{ projectTitleText }}</template>
      <template #emphasis
        ><em class="not-italic underline decoration-success/50 decoration-2 underline-offset-4">{{
          t('property.access.modalGuestCommission.headingEmphasis')
        }}</em></template
      >
    </i18n-t>
    <p class="mb-4 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalGuestCommission.body', { project: projectTitleText }) }}
    </p>

    <div class="mb-5 rounded-2xl border border-success/25 bg-success/05 px-5 py-4 text-center">
      <div class="mb-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-black-60">
        {{ t('property.access.modalGuestCommission.dealTeaserLabel') }}
      </div>
      <div class="flex items-baseline justify-center gap-1.5">
        <span class="text-[13px] font-semibold text-black-70">AED</span>
        <span class="font-serif text-[34px] font-bold leading-none text-success">
          {{ teaserAed }}<span class="text-[20px] text-success/70">+</span>
        </span>
      </div>
      <div class="mt-1 text-[11.5px] text-black-60">{{ t('property.access.modalGuestCommission.paidNote') }}</div>
    </div>

    <ul class="mb-6 space-y-2">
      <CheckBullet accent="green">{{ t('property.access.modalGuestCommission.bullet1') }}</CheckBullet>
      <CheckBullet accent="green">{{
        t('property.access.modalGuestCommission.bullet2', { percent: topSplitPercent })
      }}</CheckBullet>
      <CheckBullet accent="green">{{ t('property.access.modalGuestCommission.bullet3') }}</CheckBullet>
    </ul>

    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-black-100 px-5 text-[14px] font-bold text-white transition-colors hover:bg-black-90"
        @click="emit('signup-click')"
      >
        <span
          >{{ t('property.access.modalGuestCommission.createAccount') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('signin-click')"
      >
        {{ t('property.shell.signIn') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      {{ t('property.access.modalGuestCommission.footNote') }}
    </div>
  </ModalShell>
</template>
